import React, { useRef, useState } from 'react';
import { Check, ChevronDown, Code as CodeIcon, FileText, Link as LinkIcon, LoaderCircle, SlidersHorizontal, SquareArrowOutUpRight, Terminal } from 'lucide-react';
import { Button } from './Button.jsx';
import { CopyFallback, CopyStatus, useCopy } from './copy.jsx';
import { DesignPill } from './design.jsx';
import { DropdownContent, DropdownMenu, DropdownTrigger, MenuItem } from './Dropdown.jsx';
import { SwapText } from './Swap.jsx';
import { TooltipGroup } from './Tooltip.jsx';

function pageUrl() {
  try {
    if (window.parent && window.parent !== window) return window.parent.location.href;
  } catch { /* the preview is same-origin; a blocked parent falls back to this frame */ }
  return location.href;
}

const MCP_COMMAND = 'npx edl-docs mcp';

export function configuredPrompts() {
  try {
    const root = window.parent && window.parent !== window ? window.parent : window;
    return root.__EDL_DOCS__?.config?.prompts ?? [];
  } catch {
    return [];
  }
}

/** Args that differ from the story's initial values, the same set the URL keeps. */
export function changedArgs(args = {}, initial = {}) {
  const out = {};
  for (const [key, value] of Object.entries(args)) {
    if (value && typeof value === 'object' && value.__edlUnserialisable) continue;
    if (JSON.stringify(value) !== JSON.stringify(initial[key])) out[key] = value;
  }
  return Object.keys(out).length ? out : null;
}

export function fillPrompt(template, values) {
  return String(template).replace(/\{(markdown|url|title|story)\}/g, (_, key) => values[key] ?? '');
}

// The spinner a slow copy shows; one component so the icon swap sees one icon.
function Spinner(props) {
  return <LoaderCircle {...props} className="edl-spin" />;
}

const LINK_LABELS = { idle: 'Copy link', copied: 'Link copied' };
const COPY_KINDS = {
  markdown: { done: 'Markdown copied', field: 'the Markdown', multiline: true },
  command: { done: 'Command copied', field: 'the MCP command', multiline: false },
  prompt: { done: 'Prompt copied', field: 'the prompt', multiline: true },
};

/**
 * Copy link and Copy Markdown, in that order, on the docs page and the canvas.
 * Controls and Code follow them as grouped icon buttons on the canvas only:
 * each shows or hides its section of the story panel. Both copy buttons swap
 * their icon and label (copy.jsx, Swap.jsx); the Markdown button also reports
 * the menu's copies, naming what it copied.
 */
export function DocToolbar({ mode, entry, story, prompts = [], design, sections, onToggleSection, className }) {
  const link = useCopy();
  const markdown = useCopy();
  const [kind, setKind] = useState('markdown');
  const linkAnchor = useRef(null);
  const markdownAnchor = useRef(null);
  const componentId = entry?.componentId;
  const pageId = mode === 'story' ? entry?.id : componentId;
  const mdHref = componentId ? `/__edl/md/${componentId}.md` : null;
  const title = entry ? entry.title.split('/').pop().trim() : '';

  const pageMarkdown = async () => {
    const res = await fetch(`/__edl/md/${pageId}.md`, { cache: 'no-store' });
    if (!res.ok) throw new Error('Markdown is not available for this page.');
    let text = await res.text();
    if (mode === 'story' && story) {
      const changed = changedArgs(story.args, story.initialArgs);
      if (changed) text += `\nCurrent args: ${JSON.stringify(changed)}\n`;
    }
    return text;
  };

  // A page with nothing to copy leaves the button as it was.
  const copyAs = (next, source) => {
    setKind(next);
    markdown.copy(source, { done: COPY_KINDS[next].done });
  };
  const copyPrompt = (template) => copyAs('prompt', async () => fillPrompt(template, {
    markdown: await pageMarkdown(),
    url: pageUrl(),
    title,
    story: mode === 'story' ? entry?.name ?? '' : '',
  }));

  const markdownIcon = markdown.state === 'pending' ? Spinner : markdown.state === 'copied' ? Check : FileText;
  const markdownLabel = markdown.state === 'copied' ? COPY_KINDS[kind].done : 'Copy markdown';

  return (
    <TooltipGroup className={['ws-toolbar__tools', className].filter(Boolean).join(' ')}>
      <DesignPill link={design} />
      <span ref={linkAnchor} className="ws-copy-anchor">
        <Button
          variant="ghost"
          size="compact"
          leadingIcon={link.state === 'copied' ? Check : LinkIcon}
          className="ws-copy-link"
          onClick={() => link.copy(pageUrl(), { done: LINK_LABELS.copied })}
        >
          <SwapText>{link.state === 'copied' ? LINK_LABELS.copied : LINK_LABELS.idle}</SwapText>
        </Button>
      </span>
      <CopyStatus copy={link} />
      <CopyFallback copy={link} anchor={linkAnchor} label="the link" />
      <span ref={markdownAnchor} className="ws-copy-split">
        <Button
          variant="secondary"
          size="compact"
          leadingIcon={markdownIcon}
          className="ws-copy-markdown"
          aria-busy={markdown.state === 'pending' || undefined}
          onClick={() => copyAs('markdown', pageMarkdown)}
        >
          <SwapText>{markdownLabel}</SwapText>
        </Button>
        <DropdownMenu size="compact">
          <DropdownTrigger
            className="edl-button ws-copy-markdown__more"
            data-variant="secondary"
            data-size="compact"
            data-icon-only=""
            aria-label="More copy options"
          >
            <span className="edl-button__surface" aria-hidden="true" />
            <span className="edl-button__label">
              <ChevronDown size={14} strokeWidth={1.5} aria-hidden="true" />
            </span>
          </DropdownTrigger>
          <DropdownContent align="end" sideOffset={6}>
            {mdHref && <MenuItem index={0} icon={FileText} trailingIcon={SquareArrowOutUpRight} label="View as Markdown" href={mdHref} />}
            <MenuItem index={1} icon={Terminal} label="Copy MCP command" onSelect={() => copyAs('command', MCP_COMMAND)} />
            {prompts.map((prompt, index) => (
              <MenuItem key={prompt.label} index={index + 2} label={prompt.label} onSelect={() => copyPrompt(prompt.template)} />
            ))}
          </DropdownContent>
        </DropdownMenu>
      </span>
      <CopyStatus copy={markdown} />
      <CopyFallback copy={markdown} anchor={markdownAnchor} label={COPY_KINDS[kind].field} multiline={COPY_KINDS[kind].multiline} />
      {mode === 'story' && (
        <>
          <span className="ws-toolbar__rule" aria-hidden="true" />
          <Button
            variant="ghost"
            size="compact"
            leadingIcon={SlidersHorizontal}
            className="ws-panel-toggle"
            aria-label="Controls"
            aria-pressed={Boolean(sections?.controls)}
            onClick={() => onToggleSection?.('controls')}
            tooltip="Controls"
          />
          <Button
            variant="ghost"
            size="compact"
            leadingIcon={CodeIcon}
            className="ws-panel-toggle"
            aria-label="Code"
            aria-pressed={Boolean(sections?.code)}
            onClick={() => onToggleSection?.('code')}
            tooltip="Code"
          />
        </>
      )}
    </TooltipGroup>
  );
}
